import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, AlertTriangle, Ban, Users, MessageSquare, ArrowLeft } from 'lucide-react';

const RulesPage = () => {
  const rules = [
    {
      icon: <Ban className="w-6 h-6" />,
      title: "No Offensive Language",
      description: "Any use of offensive, abusive, or inappropriate language will result in immediate action. Repeat offenders risk permanent ban from the chatbox.",
      severity: "high"
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "No Sexual Harassment",
      description: "Sexual harassment of any kind is strictly prohibited. This includes unwanted advances, inappropriate comments, or any behavior that makes others uncomfortable. Zero tolerance policy applies.",
      severity: "high"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Respect All Members",
      description: "Treat all members with respect and courtesy. Personal attacks, bullying, discrimination, or hateful speech based on race, religion, gender, or any other characteristic will not be tolerated.",
      severity: "high"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "No Spam or Flooding",
      description: "Avoid sending repetitive messages, excessive emojis, or flooding the chat. Keep conversations meaningful and respectful of others' space.",
      severity: "medium"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "No Sharing Personal Information",
      description: "Do not share personal information (yours or others') such as phone numbers, addresses, or financial details in the public chat.",
      severity: "medium"
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "No Scams or Fraudulent Activity",
      description: "Any attempts to scam, defraud, or mislead other members will result in immediate permanent ban and potential legal action.",
      severity: "high"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Chatbox Rules & Guidelines
            </h1>
          </div>
          <p className="text-xl text-white/90">
            Please read and follow these rules to maintain a safe and respectful community
          </p>
        </div>
      </div>

      {/* Zero Tolerance Policy Banner */}
      <div className="bg-red-50 border-l-4 border-red-600 py-6">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-red-900 mb-2">
                Zero-Tolerance Policy
              </h2>
              <p className="text-red-800 text-lg">
                FXC Management Services enforces a strict zero-tolerance policy for violations of these rules. 
                Depending on the severity, violations may result in warnings, temporary suspension, 
                or permanent ban from the chatbox. We are committed to maintaining a safe, 
                professional, and respectful environment for all members.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">
            {rules.map((rule, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-6 border-l-4 ${
                  rule.severity === 'high' 
                    ? 'border-red-500' 
                    : 'border-yellow-500'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg flex-shrink-0 ${
                    rule.severity === 'high' 
                      ? 'bg-red-100 text-red-600' 
                      : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    {rule.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">
                        {index + 1}. {rule.title}
                      </h3>
                      {rule.severity === 'high' && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                          High Priority
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {rule.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Reporting Section */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue-900 mb-3">
                  Report Violations
                </h3>
                <p className="text-blue-800 mb-4">
                  If you witness any violations of these rules or feel uncomfortable with 
                  someone's behavior in the chatbox, please report it immediately to our 
                  moderation team.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-[#0d6571] text-white rounded-lg hover:bg-[#0d6571]/90 font-semibold transition-colors"
                >
                  Report to Admin
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </Link>
              </div>
            </div>
          </div>

          {/* Agreement Section */}
          <div className="mt-8 bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-gray-700 text-lg">
              By using the FXC Management Services chatbox, you agree to follow these rules and 
              understand that violations may result in disciplinary action.
            </p>
            <Link
              to="/dashboard"
              className="inline-block mt-4 px-8 py-3 bg-[#0d6571] text-white rounded-full hover:bg-[#0d6571]/90 font-semibold transition-colors"
            >
              I Understand - Return to Dashboard
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RulesPage;
